/**
 * Reading `{{person <login user id>}}` tags out of kept server text (server `PersonTag.For`,
 * contact rule 8). Kept free of the DOM so the node guard that checks the server's tag shape
 * against this reader (`term-tags-server.node.spec.ts`) can import it; `kept-text.ts` renders what
 * it finds.
 */

/** A person tag: `{{person <id>}}`, the id optional so a tag with none is caught as bad. */
const PERSON_TAG = /{{\s*person(?:\s+([.\w-]+))?\s*}}/g;

/** A login user id as the server writes it, hyphenated (C# Guid "D" format). */
const LOGIN_USER_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether `tagText` is exactly one well-formed person tag, `{{person <login user id>}}`. Lets the
 * term resolver pass a person tag through untouched for kept-text rendering, while a person tag with
 * no id or a bad one still reads as a missing term.
 */
export function isPersonTag(tagText: string): boolean {
  const match = /^{{\s*person\s+([.\w-]+)\s*}}$/.exec(tagText);
  return !!match && LOGIN_USER_ID.test(match[1]);
}

/** One run of kept text: plain text (which may hold term tags), or the id of a person tag. */
export type KeptTextPiece = { text: string } | { personId: string };

/**
 * Splits kept text at its person tags. A tag whose id is not an id stays in the text, where
 * `replaceTerms` renders it as a missing term, so a bad stored tag is visible and never throws.
 */
export function splitPersonTags(text: string): KeptTextPiece[] {
  const found: KeptTextPiece[] = [];
  let from = 0;
  for (const match of text.matchAll(PERSON_TAG)) {
    const id = match[1];
    if (!id || !LOGIN_USER_ID.test(id)) continue;
    const at = match.index;
    if (at > from) found.push({ text: text.slice(from, at) });
    found.push({ personId: id });
    from = at + match[0].length;
  }
  if (from < text.length) found.push({ text: text.slice(from) });
  return found;
}

/** The login user ids of the valid person tags in kept text, in order. */
export function personIdsIn(text: string): string[] {
  return splitPersonTags(text).flatMap((piece) =>
    'personId' in piece ? [piece.personId] : []
  );
}
